// EXPANSIÓN MÁGICA (outpainting) — política y cliente.
//
// Le agrega lienzo a una imagen ya generada hasta llegar al aspect ratio que
// elige el usuario: la foto original queda intacta y la IA pinta lo que falta
// alrededor. Cuesta 1 crédito y devuelve una imagen NUEVA (fila propia en
// `imagenes_generadas`), no una versión de la original.
//
// La geometría vive acá y no en los componentes porque la consumen las dos
// superficies —el inline de recién generadas y el historial— y encima la usa
// dos veces cada una: para decidir qué formatos ofrecer y para dibujar el
// preview. Duplicada, el preview y el botón se desincronizan.
// Ver ADR-2026-08-17-expansion-magica.

import { USE_ATLAS_BACKEND } from '../config';
import { atlasBackendFetch } from './atlasBackend';
import { GenerateAdError } from './generateAdRequest';
import { supabase } from './supabase';

export type RatioExpansion = '1:1' | '4:5' | '16:9' | '9:16';

export interface OpcionRatio {
  id: RatioExpansion;
  /** Nombre de la red donde se usa ese encuadre, que es como lo piensa el usuario. */
  etiqueta: string;
  w: number;
  h: number;
}

/** Los mismos cuatro formatos que ofrece el estudio al generar. */
export const RATIOS_EXPANSION: OpcionRatio[] = [
  { id: '1:1', etiqueta: 'Cuadrado', w: 1, h: 1 },
  { id: '4:5', etiqueta: 'Post', w: 4, h: 5 },
  { id: '16:9', etiqueta: 'Horizontal', w: 16, h: 9 },
  { id: '9:16', etiqueta: 'Historia', w: 9, h: 16 },
];

/** Lo que cuesta una expansión. Espejo del débito del backend (1 por entrega). */
export const CREDITOS_EXPANSION = 1;

/**
 * Cuánto pueden diferir dos encuadres para seguir siendo "el mismo".
 *
 * 1% cubre el redondeo de las generaciones (un 4:5 real puede venir 1024x1281) sin
 * llegar a confundir formatos distintos: el par más cercano del set, 1:1 y 4:5,
 * difiere 25%.
 */
const TOLERANCIA_RATIO = 0.01;

function ratioValido(ancho: number, alto: number): number | null {
  if (!Number.isFinite(ancho) || !Number.isFinite(alto)) return null;
  if (ancho <= 0 || alto <= 0) return null;
  return ancho / alto;
}

/**
 * Los cuatro formatos con el flag de si tiene sentido expandir a cada uno.
 *
 * Un formato queda deshabilitado cuando la imagen YA está en ese encuadre: la
 * expansión no tendría área nueva que pintar y el usuario pagaría un crédito por
 * una copia. Mientras no sepamos las medidas (la imagen todavía no cargó) no se
 * habilita ninguno, por la misma razón.
 */
export function opcionesParaImagen(
  ancho: number,
  alto: number,
): Array<OpcionRatio & { disponible: boolean }> {
  const actual = ratioValido(ancho, alto);
  return RATIOS_EXPANSION.map((op) => ({
    ...op,
    disponible: actual === null ? false : Math.abs(op.w / op.h - actual) > TOLERANCIA_RATIO * (op.w / op.h),
  }));
}

/**
 * Qué fracción del marco destino ocupa la imagen original, centrada y sin recortarse
 * (el mismo `contain` que hace el backend al expandir). Con esto el preview dibuja la
 * original nítida encima del fondo borroneado, del tamaño exacto que va a tener.
 *
 * Sin medidas devuelve el marco completo: es el estado de carga del preview, donde
 * mostrar la imagen entera es lo que menos desentona con lo que viene después.
 */
export function encuadreOriginal(
  ratio: RatioExpansion,
  ancho: number,
  alto: number,
): { anchoPct: number; altoPct: number } {
  const actual = ratioValido(ancho, alto);
  const destino = ratioDe(ratio);
  if (actual === null) return { anchoPct: 1, altoPct: 1 };
  // Más apaisada que el marco → toca los lados y sobra alto; al revés, al revés.
  if (actual > destino) return { anchoPct: 1, altoPct: destino / actual };
  return { anchoPct: actual / destino, altoPct: 1 };
}

/**
 * Hacia dónde crece el lienzo, en las palabras que va abajo del preview.
 * `null` cuando el encuadre no cambia (no hay nada que contar).
 */
export function ladoQueCrece(
  ratio: RatioExpansion,
  ancho: number,
  alto: number,
): 'costados' | 'arriba y abajo' | null {
  const actual = ratioValido(ancho, alto);
  const destino = ratioDe(ratio);
  if (actual === null) return null;
  if (Math.abs(destino - actual) <= TOLERANCIA_RATIO * destino) return null;
  return destino > actual ? 'costados' : 'arriba y abajo';
}

function ratioDe(ratio: RatioExpansion): number {
  const op = RATIOS_EXPANSION.find((r) => r.id === ratio);
  return op ? op.w / op.h : 1;
}

/**
 * La herramienta se ofrece SOLO contra el backend nuevo: el modo `expandir` no
 * existe en el webhook viejo de n8n, así que con el flag apagado el fallback
 * devolvería una edición cualquiera (cobrada). Mejor no mostrarla.
 */
export const EXPANSION_DISPONIBLE = USE_ATLAS_BACKEND;

export interface ImagenExpandida {
  id: string;
  fileName: string;
  signedUrl: string;
}

const MENSAJE_SIN_CREDITOS =
  'No te alcanzan los créditos para esta expansión. Sumá créditos o actualizá tu plan para continuar.';

/**
 * Expande una imagen y devuelve la fila NUEVA ya firmada, lista para mostrar.
 *
 * Va derecho a atlas (sin la rama n8n de `postImageEdit`) y espera la respuesta
 * síncrona del endpoint, igual que el resto del Editor IA. Tira `GenerateAdError`
 * de tipo billing en 402 para que la UI abra el modal de saldo de siempre.
 */
export async function expandirImagen(params: {
  /** UUID de la fila de `imagenes_generadas`. Una de las dos fuentes es obligatoria. */
  imagenId?: string;
  /** Foto SUBIDA por el usuario (sin fila): referencia de storage, igual que el
   *  carrusel — el backend descarga por bucket+path, nunca por URL firmada
   *  (ADR-2026-08-25-expansion-magica-atajo-home). */
  subida?: { bucket: string; path: string };
  aspectRatio: RatioExpansion;
  companyId: string;
  /** Etiqueta de analytics de la superficie desde la que se expandió. */
  modoUtilizado?: string;
}): Promise<ImagenExpandida> {
  const res = await atlasBackendFetch('/v1/images/edit', {
    ...(params.imagenId ? { imagen_id: params.imagenId } : {}),
    ...(params.subida
      ? { image_bucket: params.subida.bucket, image_path: params.subida.path }
      : {}),
    mode: 'expandir',
    aspect_ratio: params.aspectRatio,
    modo_utilizado: params.modoUtilizado ?? 'expansion_magica',
  });
  if (res.status === 402) {
    throw new GenerateAdError('billing', MENSAJE_SIN_CREDITOS, 'billing');
  }
  // El backend manda su propio mensaje en castellano (`userFacingMessage`) hasta en
  // los 500 — es más útil que un genérico, así que se intenta leer antes de rendirse.
  const data = (await res.json().catch(() => null)) as {
    response?: string;
    edited_image_id?: string | number;
    error?: string;
  } | null;
  if (!res.ok) {
    throw new Error(data?.error || 'No pudimos expandir la imagen. Intentá de nuevo.');
  }
  // Contrato del Editor IA: la moderación llega como 200 con shape propio.
  if (data?.response === 'error_moderation') {
    throw new Error(
      'La imagen no pasó el filtro de contenido al expandirse. Probá con otra.',
    );
  }
  if (data?.response !== 'success' || !data.edited_image_id) {
    throw new Error(data?.error || 'No pudimos expandir la imagen. Intentá de nuevo.');
  }

  const fila = await resolverImagenGenerada(String(data.edited_image_id), params.companyId);
  if (!fila) {
    throw new Error('La imagen se expandió pero no pudimos cargarla. Mirá el historial.');
  }
  const { data: firmada } = await supabase.storage
    .from('generadas')
    .createSignedUrl(`${params.companyId}/full/${fila.file_name}`, 3600);
  if (!firmada?.signedUrl) {
    throw new Error('La imagen se expandió pero no pudimos cargarla. Mirá el historial.');
  }
  return { id: fila.id, fileName: fila.file_name, signedUrl: firmada.signedUrl };
}

/**
 * El backend devuelve `edited_image_id`, que históricamente puede ser el UUID de la
 * fila o su `file_name` (con o sin extensión) — el Editor IA resuelve las tres formas
 * desde n8n y acá pasa lo mismo. Siempre acotado a la empresa.
 */
async function resolverImagenGenerada(
  idOFileName: string,
  companyId: string,
): Promise<{ id: string; file_name: string } | null> {
  // UUID: el caso normal del backend nuevo.
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(idOFileName)) {
    const { data } = await supabase
      .from('imagenes_generadas')
      .select('id, file_name')
      .eq('id', idOFileName)
      .eq('id_company', companyId)
      .maybeSingle();
    if (data) return data as { id: string; file_name: string };
  }
  const { data: exacta } = await supabase
    .from('imagenes_generadas')
    .select('id, file_name')
    .eq('file_name', idOFileName)
    .eq('id_company', companyId)
    .maybeSingle();
  if (exacta) return exacta as { id: string; file_name: string };

  const { data: porPrefijo } = await supabase
    .from('imagenes_generadas')
    .select('id, file_name')
    .like('file_name', `${idOFileName}.%`)
    .eq('id_company', companyId)
    .maybeSingle();
  return (porPrefijo as { id: string; file_name: string } | null) ?? null;
}
